/**
 * Identification Document Types
 *
 * Types for recruit identity verification documents.
 * Requires two forms of valid government-issued identification.
 */

export type IDType =
  | 'drivers_license'
  | 'state_id'
  | 'military_id'
  | 'us_passport'
  | 'permanent_resident_card'
  | 'ssn_card';

/**
 * Status of an ID document review
 * - pending: Uploaded but not reviewed, or rejected and re-uploaded
 * - approved: Admin approved this ID
 * - rejected: Admin rejected with reason
 */
export type IDStatus = 'pending' | 'approved' | 'rejected';

export interface IDOption {
  value: IDType;
  label: string;
  requiresBack: boolean;
}

export const ID_OPTIONS: IDOption[] = [
  { value: 'drivers_license', label: "Driver's License", requiresBack: true },
  { value: 'state_id', label: 'State ID', requiresBack: true },
  { value: 'military_id', label: 'Military ID', requiresBack: true },
  { value: 'us_passport', label: 'US Passport', requiresBack: false },
  { value: 'permanent_resident_card', label: 'Permanent Resident Card', requiresBack: true },
  { value: 'ssn_card', label: 'Social Security Card', requiresBack: true }
];

export interface RecruitIdentification {
  id_primary_type: IDType | null;
  id_primary_front_path: string | null;
  id_primary_back_path: string | null;
  id_secondary_type: IDType | null;
  id_secondary_front_path: string | null;
  id_secondary_back_path: string | null;
  id_validated_at: string | null;
  id_validated_by: string | null;

  // Per-ID status tracking
  id_primary_status: IDStatus | null;
  id_primary_approved_at: string | null;
  id_primary_approved_by: string | null;
  id_primary_rejected_at: string | null;
  id_primary_rejected_by: string | null;
  id_primary_rejection_reason: string | null;

  id_secondary_status: IDStatus | null;
  id_secondary_approved_at: string | null;
  id_secondary_approved_by: string | null;
  id_secondary_rejected_at: string | null;
  id_secondary_rejected_by: string | null;
  id_secondary_rejection_reason: string | null;
}

/**
 * Get the label for an ID type
 */
export function getIDLabel(type: IDType | null): string {
  if (!type) {
return '';
}
  return ID_OPTIONS.find(opt => opt.value === type)?.label || type;
}

/**
 * Check if an ID type requires a back image
 */
export function idRequiresBack(type: IDType | null): boolean {
  if (!type) {
return false;
}
  return ID_OPTIONS.find(opt => opt.value === type)?.requiresBack ?? false;
}

/**
 * Check if primary ID is complete (has required images)
 */
export function isPrimaryIDComplete(identification: RecruitIdentification): boolean {
  if (!identification.id_primary_type || !identification.id_primary_front_path) {
    return false;
  }
  if (idRequiresBack(identification.id_primary_type) && !identification.id_primary_back_path) {
    return false;
  }
  return true;
}

/**
 * Check if secondary ID is complete (has required images)
 */
export function isSecondaryIDComplete(identification: RecruitIdentification): boolean {
  if (!identification.id_secondary_type || !identification.id_secondary_front_path) {
    return false;
  }
  if (idRequiresBack(identification.id_secondary_type) && !identification.id_secondary_back_path) {
    return false;
  }
  return true;
}

/**
 * Check if all identification documents are complete
 */
export function areAllIDsComplete(identification: RecruitIdentification): boolean {
  return isPrimaryIDComplete(identification) && isSecondaryIDComplete(identification);
}

/**
 * Check if identification has been validated by admin
 */
export function isIDValidated(identification: RecruitIdentification): boolean {
  return !!identification.id_validated_at && !!identification.id_validated_by;
}
